import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from 'antd';
import { Icon as IconifyIcon } from '@iconify/react';

import { ClipboardService } from '../../../shared/services/ClipboardService';
import { revealGasDoPovoCpf } from '../../../shared/services/http/PedidoService';

const CPF_MASK = '•••.•••.•••-••';

export const formatCpf = (cpf) => {
  const digits = (cpf || '').replace(/\D/g, '');
  if (digits.length !== 11) return cpf || CPF_MASK;
  return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
};

const CpfGasDoPovoField = ({
  orderId,
  compact = false,
  autoReveal = false,
}) => {
  const [cpf, setCpf] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const requestVersion = useRef(0);

  const reveal = useCallback(async () => {
    const currentRequest = ++requestVersion.current;
    setLoading(true);
    setError('');
    try {
      const result = await revealGasDoPovoCpf(orderId);
      if (!result?.cpf) throw new Error('CPF indisponível ou expirado.');
      if (requestVersion.current === currentRequest) setCpf(result.cpf);
    } catch (requestError) {
      if (requestVersion.current !== currentRequest) return;
      setCpf('');
      setError(
        typeof requestError === 'string'
          ? requestError
          : 'CPF indisponível ou expirado.',
      );
    } finally {
      if (requestVersion.current === currentRequest) setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    requestVersion.current += 1;
    setCpf('');
    setError('');
    setLoading(false);

    if (autoReveal && orderId) reveal();

    return () => {
      requestVersion.current += 1;
    };
  }, [autoReveal, orderId, reveal]);

  return (
    <div className={`dp-cpf-reveal${compact ? ' dp-cpf-reveal--compact' : ''}`}>
      <div className='dp-cpf-reveal__value'>
        <span>CPF Gás do Povo:</span>
        <strong aria-label={cpf ? 'CPF revelado' : 'CPF oculto'}>
          {cpf ? formatCpf(cpf) : CPF_MASK}
        </strong>
      </div>

      <div className='dp-cpf-reveal__actions'>
        {!cpf && !autoReveal ? (
          <Button
            type='link'
            size='small'
            loading={loading}
            disabled={!orderId}
            onClick={reveal}>
            <IconifyIcon icon='fa6-solid:eye' />
            Revelar CPF
          </Button>
        ) : cpf ? (
          <Button
            type='link'
            size='small'
            onClick={() => ClipboardService().copy(cpf)}>
            <IconifyIcon icon='ph:copy' />
            Copiar
          </Button>
        ) : null}
      </div>

      {loading && <small className='dp-cpf-reveal__notice'>Carregando CPF...</small>}
      <small className='dp-cpf-reveal__notice'>
        Uso exclusivo para validação do benefício. Acesso auditado.
      </small>
      {error && (
        <small className='dp-cpf-reveal__error' role='alert'>
          {error}
        </small>
      )}
    </div>
  );
};

export default CpfGasDoPovoField;
